import {
  type AvaaioLanguageCode,
  AVAAIO_LANGUAGES,
  getInitialPreferredLanguage,
  persistPreferredLanguage,
} from "../utils/avaaioLanguage";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

type RegistrationClosedCopy = {
  eyebrow: string;
  title: string;
  message: string;
  launch: string;
  waitlist: string;
  waitlistNote: string;
  signIn: string;
  language: string;
};

const COPY: Record<
  AvaaioLanguageCode,
  RegistrationClosedCopy
> = {
  en: {
    eyebrow: "AVAAIO EARLY ACCESS",
    title: "Registration is not open yet",
    message:
      "AVAAIO is preparing for its official release. New public account registration is temporarily closed.",
    launch: "Official release: December 13, 2026",
    waitlist: "Go to the AVAAIO waitlist",
    waitlistNote:
      "Return to the sign-in page to join the verified early-access waitlist and select your preferred email language.",
    signIn: "Back to Sign In",
    language: "Display language",
  },
  es: {
    eyebrow: "ACCESO ANTICIPADO DE AVAAIO",
    title: "El registro aún no está abierto",
    message:
      "AVAAIO se está preparando para su lanzamiento oficial. El registro público de cuentas está temporalmente cerrado.",
    launch: "Lanzamiento oficial: 13 de diciembre de 2026",
    waitlist: "Ir a la lista de espera de AVAAIO",
    waitlistNote:
      "Vuelve a la página de inicio de sesión para unirte a la lista de espera verificada y elegir el idioma de los correos.",
    signIn: "Volver a iniciar sesión",
    language: "Idioma de visualización",
  },
  fr: {
    eyebrow: "ACCÈS ANTICIPÉ AVAAIO",
    title: "Les inscriptions ne sont pas encore ouvertes",
    message:
      "AVAAIO prépare son lancement officiel. La création publique de comptes est temporairement fermée.",
    launch: "Lancement officiel : 13 décembre 2026",
    waitlist: "Accéder à la liste d’attente AVAAIO",
    waitlistNote:
      "Revenez à la page de connexion pour rejoindre la liste d’attente vérifiée et choisir la langue des e-mails.",
    signIn: "Retour à la connexion",
    language: "Langue d’affichage",
  },
  de: {
    eyebrow: "AVAAIO FRÜHZEITIGER ZUGANG",
    title: "Die Registrierung ist noch nicht geöffnet",
    message:
      "AVAAIO bereitet den offiziellen Start vor. Die öffentliche Kontoerstellung ist vorübergehend geschlossen.",
    launch: "Offizieller Start: 13. Dezember 2026",
    waitlist: "Zur AVAAIO-Warteliste",
    waitlistNote:
      "Kehren Sie zur Anmeldeseite zurück, um der verifizierten Warteliste beizutreten und Ihre E-Mail-Sprache auszuwählen.",
    signIn: "Zurück zur Anmeldung",
    language: "Anzeigesprache",
  },
  it: {
    eyebrow: "ACCESSO ANTICIPATO AVAAIO",
    title: "Le registrazioni non sono ancora aperte",
    message:
      "AVAAIO si sta preparando per il lancio ufficiale. La registrazione pubblica degli account è temporaneamente chiusa.",
    launch: "Lancio ufficiale: 13 dicembre 2026",
    waitlist: "Vai alla lista d’attesa AVAAIO",
    waitlistNote:
      "Torna alla pagina di accesso per unirti alla lista d’attesa verificata e scegliere la lingua delle email.",
    signIn: "Torna all’accesso",
    language: "Lingua di visualizzazione",
  },
  pt: {
    eyebrow: "ACESSO ANTECIPADO AVAAIO",
    title: "O cadastro ainda não está aberto",
    message:
      "O AVAAIO está se preparando para o lançamento oficial. O cadastro público de contas está temporariamente fechado.",
    launch: "Lançamento oficial: 13 de dezembro de 2026",
    waitlist: "Ir para a lista de espera do AVAAIO",
    waitlistNote:
      "Volte à página de entrada para participar da lista de espera verificada e selecionar o idioma dos emails.",
    signIn: "Voltar para entrar",
    language: "Idioma de exibição",
  },
};

export default function RegisterClosed() {
  const navigate = useNavigate();

  const [language, setLanguage] =
    useState<AvaaioLanguageCode>(
      getInitialPreferredLanguage()
    );

  const copy = useMemo(
    () => COPY[language],
    [language]
  );

  const returnToLogin = () => {
    navigate("/login");
  };

  return (
    <main style={styles.page}>
      <div style={styles.glow} />

      <section
        style={styles.card}
        aria-labelledby="registration-closed-title"
      >
        <div style={styles.brand}>AVAAIO™</div>

        <div style={styles.eyebrow}>
          {copy.eyebrow}
        </div>

        <h1
          id="registration-closed-title"
          style={styles.title}
        >
          {copy.title}
        </h1>

        <p style={styles.message}>
          {copy.message}
        </p>

        <div style={styles.launch}>
          {copy.launch}
        </div>

        <label
          htmlFor="registration-language"
          style={styles.label}
        >
          {copy.language}
        </label>

        <select
          id="registration-language"
          value={language}
          style={styles.select}
          onChange={(event) => {
            const next =
              event.target.value as
                AvaaioLanguageCode;

            setLanguage(next);
            persistPreferredLanguage(next);
          }}
        >
          {AVAAIO_LANGUAGES.map((item) => (
            <option
              key={item.code}
              value={item.code}
            >
              {item.nativeLabel}
            </option>
          ))}
        </select>

        <button
          type="button"
          style={styles.primaryButton}
          onClick={returnToLogin}
        >
          {copy.waitlist}
        </button>

        <p style={styles.note}>
          {copy.waitlistNote}
        </p>

        <button
          type="button"
          style={styles.secondaryButton}
          onClick={returnToLogin}
        >
          {copy.signIn}
        </button>
      </section>
    </main>
  );
}

const styles: Record<
  string,
  React.CSSProperties
> = {
  page: {
    minHeight: "100dvh",
    display: "grid",
    placeItems: "center",
    position: "relative",
    overflow: "hidden",
    boxSizing: "border-box",
    padding:
      "max(24px, env(safe-area-inset-top)) 18px max(24px, env(safe-area-inset-bottom))",
    background:
      "radial-gradient(circle at 50% 20%, rgba(0, 214, 255, 0.16), transparent 34%), linear-gradient(160deg, #020710, #071523 58%, #02060d)",
    color: "#f4fdff",
  },
  glow: {
    position: "absolute",
    width: "430px",
    height: "430px",
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(37, 224, 255, 0.16), transparent 68%)",
    filter: "blur(10px)",
    pointerEvents: "none",
  },
  card: {
    width: "min(100%, 480px)",
    position: "relative",
    zIndex: 1,
    boxSizing: "border-box",
    padding: "30px 24px",
    border:
      "1px solid rgba(82, 225, 255, 0.35)",
    borderRadius: "24px",
    background:
      "linear-gradient(145deg, rgba(8, 27, 43, 0.94), rgba(3, 12, 23, 0.96))",
    boxShadow:
      "0 24px 80px rgba(0, 0, 0, 0.5), 0 0 35px rgba(28, 211, 255, 0.12)",
    textAlign: "center",
  },
  brand: {
    fontSize: "1.7rem",
    fontWeight: 950,
    letterSpacing: "0.16em",
    color: "#f6ffff",
    textShadow:
      "0 0 18px rgba(72, 229, 255, 0.7)",
  },
  eyebrow: {
    marginTop: "18px",
    color: "#5be7ff",
    fontSize: "0.72rem",
    fontWeight: 900,
    letterSpacing: "0.14em",
  },
  title: {
    margin: "12px 0 10px",
    fontSize: "clamp(1.65rem, 7vw, 2.25rem)",
    lineHeight: 1.1,
  },
  message: {
    margin: 0,
    color: "rgba(226, 247, 252, 0.82)",
    lineHeight: 1.65,
    fontSize: "0.98rem",
  },
  launch: {
    margin: "20px 0",
    padding: "12px",
    borderRadius: "13px",
    border:
      "1px solid rgba(255, 215, 92, 0.38)",
    background:
      "rgba(255, 206, 61, 0.08)",
    color: "#ffe495",
    fontWeight: 850,
  },
  label: {
    display: "block",
    marginBottom: "7px",
    textAlign: "left",
    color: "rgba(232, 252, 255, 0.88)",
    fontSize: "0.83rem",
    fontWeight: 750,
  },
  select: {
    width: "100%",
    minHeight: "46px",
    boxSizing: "border-box",
    padding: "10px 12px",
    borderRadius: "12px",
    border:
      "1px solid rgba(81, 224, 255, 0.48)",
    background: "#091827",
    color: "#ffffff",
    fontSize: "16px",
    colorScheme: "dark",
  },
  primaryButton: {
    width: "100%",
    minHeight: "48px",
    marginTop: "18px",
    borderRadius: "13px",
    border:
      "1px solid rgba(82, 230, 255, 0.7)",
    background:
      "linear-gradient(135deg, rgba(18, 178, 213, 0.8), rgba(20, 91, 152, 0.9))",
    color: "#ffffff",
    fontSize: "0.98rem",
    fontWeight: 900,
    cursor: "pointer",
  },
  note: {
    margin: "13px 0",
    color: "rgba(193, 225, 234, 0.72)",
    fontSize: "0.79rem",
    lineHeight: 1.5,
  },
  secondaryButton: {
    width: "100%",
    minHeight: "44px",
    borderRadius: "13px",
    border:
      "1px solid rgba(159, 216, 231, 0.28)",
    background: "rgba(7, 22, 36, 0.58)",
    color: "#dffaff",
    fontSize: "0.9rem",
    fontWeight: 800,
    cursor: "pointer",
  },
};
